/**
 * Voice Command Executor Service
 * Enterprise-grade voice command execution with database connectivity
 * 
 * This service bridges voice command parsing with actual app actions,
 * providing consistent execution, voice feedback, and analytics.
 * 
 * @module VoiceCommandExecutor
 * @version 1.0.0
 */

import { textToSpeech } from '../voice/textToSpeech';
import { getCompanyId } from '../tenantContext';
import { timeClockService } from '../services/timeClockService';
import { productionService } from '../services/productionService';
import { serviceTicketService } from '../services/serviceTicketService';
import { taskService } from '../tasks/taskService';
import { notificationService } from '../services/notificationService';
import { db } from '../firebase/client';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { toast } from 'sonner';

// ============================================================================
// TYPES
// ============================================================================

export interface VoiceCommandResult {
  success: boolean;
  action: string;
  message: string;
  data?: any;
  speakResponse: boolean;
  toastType?: 'success' | 'error' | 'info' | 'warning';
}

export interface VoiceCommandContext {
  userId: string;
  companyId: string;
  userRole: string;
  userName: string;
  currentWell?: any;
  wells: any[];
  location?: { lat: number; lng: number };
}

export interface WellStatusReport {
  wellId: string;
  wellName: string;
  status: string;
  lastCheck?: string;
  lastProduction?: {
    oil: number;
    water: number;
    gas: number;
    date: string;
  };
  alerts: string[];
}

// ============================================================================
// VOICE COMMAND EXECUTOR CLASS
// ============================================================================

class VoiceCommandExecutorService {
  private context: VoiceCommandContext | null = null;

  /**
   * Update execution context
   */
  setContext(ctx: Partial<VoiceCommandContext>) {
    this.context = { ...this.context, ...ctx } as VoiceCommandContext;
  }

  /**
   * Get current context
   */
  getContext(): VoiceCommandContext | null {
    return this.context;
  }

  // ==========================================================================
  // TIME CLOCK COMMANDS
  // ==========================================================================

  /**
   * Execute clock in command
   */
  async clockIn(): Promise<VoiceCommandResult> {
    if (!this.context?.userId || !this.context?.companyId) {
      return this.error('clock_in', 'User context not available');
    }

    try {
      // Check if already clocked in
      const activeEntry = await timeClockService.getActiveEntry(this.context.userId);
      if (activeEntry) {
        const response = `You're already clocked in since ${this.formatTime(activeEntry.clockIn)}`;
        await this.speak(response);
        return {
          success: false,
          action: 'clock_in',
          message: response,
          speakResponse: true,
          toastType: 'warning'
        };
      }

      // Get current location
      const location = await this.getCurrentLocation();

      // Clock in
      const entry = await timeClockService.clockIn(
        this.context.userId,
        location,
        'Clocked in via voice command'
      );

      const response = `You're now clocked in at ${this.formatTime(entry.clockIn)}. Have a productive day!`;
      await this.speak(response);

      return {
        success: true,
        action: 'clock_in',
        message: response,
        data: entry,
        speakResponse: true,
        toastType: 'success'
      };
    } catch (error: any) {
      console.error('Clock in error:', error);
      return this.error('clock_in', error.message || 'Failed to clock in');
    }
  }

  /**
   * Execute clock out command
   */
  async clockOut(): Promise<VoiceCommandResult> {
    if (!this.context?.userId || !this.context?.companyId) {
      return this.error('clock_out', 'User context not available');
    }

    try {
      // Get active entry
      const activeEntry = await timeClockService.getActiveEntry(this.context.userId);
      if (!activeEntry) {
        const response = "You're not currently clocked in.";
        await this.speak(response);
        return {
          success: false,
          action: 'clock_out',
          message: response,
          speakResponse: true,
          toastType: 'warning'
        };
      }

      // Get current location
      const location = await this.getCurrentLocation();

      // Calculate estimated mileage (if wells visited)
      const mileage = activeEntry.wellsVisited?.length ? activeEntry.wellsVisited.length * 5 : 0;

      // Clock out
      const entry = await timeClockService.clockOut(
        this.context.userId,
        activeEntry.id,
        location,
        mileage,
        'Clocked out via voice command'
      );

      const hoursWorked = entry.hoursWorked?.toFixed(1) || '0';
      const response = `You're now clocked out. You worked ${hoursWorked} hours today. Great work!`;
      await this.speak(response);

      return {
        success: true,
        action: 'clock_out',
        message: response,
        data: entry,
        speakResponse: true,
        toastType: 'success'
      };
    } catch (error: any) {
      console.error('Clock out error:', error);
      return this.error('clock_out', error.message || 'Failed to clock out');
    }
  }

  /**
   * Get time clock status
   */
  async getTimeStatus(): Promise<VoiceCommandResult> {
    if (!this.context?.userId) {
      return this.error('time_status', 'User context not available');
    }

    try {
      const activeEntry = await timeClockService.getActiveEntry(this.context.userId);
      
      if (activeEntry) {
        const clockedInAt = new Date(activeEntry.clockIn);
        const now = new Date();
        const hoursWorked = ((now.getTime() - clockedInAt.getTime()) / (1000 * 60 * 60)).toFixed(1);
        
        const response = `You've been clocked in since ${this.formatTime(activeEntry.clockIn)}. That's ${hoursWorked} hours so far.`;
        await this.speak(response);
        
        return {
          success: true,
          action: 'time_status',
          message: response,
          data: { hoursWorked, clockedInAt: activeEntry.clockIn },
          speakResponse: true,
          toastType: 'info'
        };
      } else {
        const response = "You're currently not clocked in.";
        await this.speak(response);
        
        return {
          success: true,
          action: 'time_status',
          message: response,
          speakResponse: true,
          toastType: 'info'
        };
      }
    } catch (error: any) {
      return this.error('time_status', error.message);
    }
  }

  // ==========================================================================
  // WELL STATUS QUERIES
  // ==========================================================================

  /**
   * Get status of a specific well
   */
  async getWellStatus(wellId: string): Promise<VoiceCommandResult> {
    if (!this.context?.companyId) {
      return this.error('well_status', 'Company context not available');
    }

    try {
      // Find the well
      const well = this.findWell(wellId);
      if (!well) {
        const response = `I couldn't find a well matching "${wellId}".`;
        await this.speak(response);
        return {
          success: false,
          action: 'well_status',
          message: response,
          speakResponse: true,
          toastType: 'error'
        };
      }

      // Get latest production
      let latestProduction: any = null;
      try {
        const prodRef = collection(db, `companies/${this.context.companyId}/wells/${well.id}/production_entries`);
        const prodQuery = query(prodRef, orderBy('entryDate', 'desc'), limit(1));
        const prodSnap = await getDocs(prodQuery);
        if (!prodSnap.empty) {
          latestProduction = prodSnap.docs[0].data();
        }
      } catch (e) {
        // Ignore production fetch errors
      }

      // Build status response
      const statusText = this.formatWellStatus(well.status);
      let response = `${well.name} is ${statusText}.`;

      if (latestProduction) {
        const oil = latestProduction.oilVolume || latestProduction.oil_volume_bbl || 0;
        const water = latestProduction.waterVolume || latestProduction.water_volume_bbl || 0;
        response += ` Last production: ${oil} barrels oil, ${water} barrels water.`;
      }

      if (well.lastCheck) {
        response += ` Last checked ${this.formatRelativeTime(well.lastCheck)}.`;
      }

      await this.speak(response);

      return {
        success: true,
        action: 'well_status',
        message: response,
        data: { well, latestProduction },
        speakResponse: true,
        toastType: 'info'
      };
    } catch (error: any) {
      return this.error('well_status', error.message);
    }
  }

  /**
   * Get list of wells needing attention
   */
  async getWellsNeedingAttention(): Promise<VoiceCommandResult> {
    if (!this.context?.wells) {
      return this.error('wells_attention', 'Wells data not available');
    }

    try {
      const overdueWells = this.context.wells.filter(w => {
        if (!w.lastCheck) return true;
        const lastCheck = new Date(w.lastCheck);
        const daysSince = (Date.now() - lastCheck.getTime()) / (1000 * 60 * 60 * 24);
        return daysSince > (w.checkFrequencyDays || 1);
      });

      const issueWells = this.context.wells.filter(w => 
        w.status === 'down' || w.status === 'maintenance' || w.hasAlert
      );

      const uniqueWells = [...new Map([...overdueWells, ...issueWells].map(w => [w.id, w])).values()];

      let response: string;
      if (uniqueWells.length === 0) {
        response = 'All wells are in good standing. No immediate attention required.';
      } else if (uniqueWells.length === 1) {
        response = `One well needs attention: ${uniqueWells[0].name}. ${this.formatWellIssue(uniqueWells[0])}`;
      } else if (uniqueWells.length <= 3) {
        const names = uniqueWells.map(w => w.name).join(', ');
        response = `${uniqueWells.length} wells need attention: ${names}.`;
      } else {
        response = `${uniqueWells.length} wells need attention. The most critical are ${uniqueWells.slice(0, 3).map(w => w.name).join(', ')}.`;
      }

      await this.speak(response);

      return {
        success: true,
        action: 'wells_attention',
        message: response,
        data: { wells: uniqueWells, count: uniqueWells.length },
        speakResponse: true,
        toastType: uniqueWells.length > 0 ? 'warning' : 'success'
      };
    } catch (error: any) {
      return this.error('wells_attention', error.message);
    }
  }

  /**
   * Get production summary
   */
  async getProductionSummary(timeframe: 'today' | 'yesterday' | 'week' = 'today'): Promise<VoiceCommandResult> {
    if (!this.context?.companyId) {
      return this.error('production_summary', 'Company context not available');
    }

    try {
      let totalOil = 0;
      let totalWater = 0;
      let totalGas = 0;
      let entriesCount = 0;

      const now = new Date();
      let startDate: Date;
      
      if (timeframe === 'yesterday') {
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 1);
        startDate.setHours(0, 0, 0, 0);
      } else if (timeframe === 'week') {
        startDate = new Date(now);
        startDate.setDate(startDate.getDate() - 7);
      } else {
        startDate = new Date(now);
        startDate.setHours(0, 0, 0, 0);
      }

      // Query production for all wells
      for (const well of (this.context.wells || [])) {
        try {
          const prodRef = collection(db, `companies/${this.context.companyId}/wells/${well.id}/production_entries`);
          const prodQuery = query(
            prodRef,
            where('entryDate', '>=', startDate.toISOString().split('T')[0]),
            orderBy('entryDate', 'desc')
          );
          const prodSnap = await getDocs(prodQuery);
          
          prodSnap.docs.forEach(doc => {
            const data = doc.data();
            totalOil += data.oilVolume || data.oil_volume_bbl || 0;
            totalWater += data.waterVolume || data.water_volume_bbl || 0;
            totalGas += data.gasVolume || data.gas_volume_mcf || 0;
            entriesCount++;
          });
        } catch (e) {
          // Skip wells with query errors
        }
      }

      const timeframeText = timeframe === 'today' ? 'Today' : timeframe === 'yesterday' ? 'Yesterday' : 'This week';
      let response: string;

      if (entriesCount === 0) {
        response = `No production entries found for ${timeframeText.toLowerCase()}.`;
      } else {
        response = `${timeframeText}'s production: ${Math.round(totalOil)} barrels of oil, ${Math.round(totalWater)} barrels of water`;
        if (totalGas > 0) {
          response += `, and ${Math.round(totalGas)} MCF of gas`;
        }
        response += `.`;
      }

      await this.speak(response);

      return {
        success: true,
        action: 'production_summary',
        message: response,
        data: { oil: totalOil, water: totalWater, gas: totalGas, entries: entriesCount },
        speakResponse: true,
        toastType: 'info'
      };
    } catch (error: any) {
      return this.error('production_summary', error.message);
    }
  }

  // ==========================================================================
  // TASK & TICKET QUERIES
  // ==========================================================================

  /**
   * Get open tickets summary
   */
  async getOpenTickets(): Promise<VoiceCommandResult> {
    if (!this.context?.companyId) {
      return this.error('open_tickets', 'Company context not available');
    }

    try {
      const tickets = await serviceTicketService.getOpenTickets();
      
      let response: string;
      if (tickets.length === 0) {
        response = 'No open service tickets. All clear!';
      } else {
        const critical = tickets.filter(t => t.priority === 'critical' || t.priority === 'high');
        
        if (critical.length > 0) {
          response = `You have ${tickets.length} open tickets, ${critical.length} are high priority.`;
          if (critical.length <= 2) {
            response += ` Priority tickets: ${critical.map(t => t.title || t.wellName).join(' and ')}.`;
          }
        } else {
          response = `You have ${tickets.length} open service tickets.`;
        }
      }

      await this.speak(response);

      return {
        success: true,
        action: 'open_tickets',
        message: response,
        data: { tickets, count: tickets.length },
        speakResponse: true,
        toastType: tickets.length > 0 ? 'info' : 'success'
      };
    } catch (error: any) {
      return this.error('open_tickets', error.message);
    }
  }

  /**
   * Get my tasks
   */
  async getMyTasks(): Promise<VoiceCommandResult> {
    if (!this.context?.userId) {
      return this.error('my_tasks', 'User context not available');
    }

    try {
      const tasks = await taskService.getTasksByUser(this.context.userId);
      const pendingTasks = tasks.filter(t => t.status !== 'completed');
      const overdueTasks = pendingTasks.filter(t => {
        if (!t.dueDate) return false;
        return new Date(t.dueDate) < new Date();
      });

      let response: string;
      if (pendingTasks.length === 0) {
        response = 'You have no pending tasks. Nice work!';
      } else if (overdueTasks.length > 0) {
        response = `You have ${pendingTasks.length} pending tasks, and ${overdueTasks.length} are overdue.`;
      } else {
        response = `You have ${pendingTasks.length} pending tasks.`;
      }

      await this.speak(response);

      return {
        success: true,
        action: 'my_tasks',
        message: response,
        data: { tasks: pendingTasks, overdue: overdueTasks },
        speakResponse: true,
        toastType: overdueTasks.length > 0 ? 'warning' : 'info'
      };
    } catch (error: any) {
      return this.error('my_tasks', error.message);
    }
  }

  // ==========================================================================
  // SYNC & DATA OPERATIONS
  // ==========================================================================

  /**
   * Force data sync
   */
  async syncData(onRefresh: () => Promise<void>): Promise<VoiceCommandResult> {
    try {
      await this.speak('Syncing your data now.');
      
      await onRefresh();
      
      const response = 'Data sync complete. Everything is up to date.';
      await this.speak(response);

      return {
        success: true,
        action: 'sync_data',
        message: response,
        speakResponse: true,
        toastType: 'success'
      };
    } catch (error: any) {
      return this.error('sync_data', error.message);
    }
  }

  // ==========================================================================
  // ROUTE QUERIES
  // ==========================================================================

  /**
   * Get route progress
   */
  async getRouteProgress(completedWells: string[], totalWells: number): Promise<VoiceCommandResult> {
    const completed = completedWells.length;
    const remaining = totalWells - completed;
    
    let response: string;
    if (completed === 0) {
      response = `You have ${totalWells} wells to check today. Ready to start?`;
    } else if (remaining === 0) {
      response = `Excellent! You've completed all ${totalWells} wells on your route!`;
    } else {
      const percentage = Math.round((completed / totalWells) * 100);
      response = `You've completed ${completed} of ${totalWells} wells. ${remaining} remaining. That's ${percentage}% done.`;
    }

    await this.speak(response);

    return {
      success: true,
      action: 'route_progress',
      message: response,
      data: { completed, remaining, total: totalWells },
      speakResponse: true,
      toastType: 'info'
    };
  }

  // ==========================================================================
  // HELPER METHODS
  // ==========================================================================

  /**
   * Find well by identifier (ID, name, or partial match)
   */
  private findWell(identifier: string): any | null {
    if (!this.context?.wells) return null;

    const normalizedId = identifier.toLowerCase().trim();
    
    return this.context.wells.find(w => {
      // Exact ID match
      if (w.id?.toLowerCase() === normalizedId) return true;
      
      // Exact name match
      if (w.name?.toLowerCase() === normalizedId) return true;
      
      // Well number match (e.g., "42" matches "Well 42" or "042")
      const numberMatch = normalizedId.match(/\d+/);
      if (numberMatch) {
        const num = numberMatch[0];
        if (w.name?.toLowerCase().includes(num)) return true;
        if (w.wellNumber === num || w.wellNumber === parseInt(num)) return true;
      }
      
      // Partial name match
      if (w.name?.toLowerCase().includes(normalizedId)) return true;
      
      return false;
    });
  }

  /**
   * Get current GPS location
   */
  private async getCurrentLocation(): Promise<{ lat: number; lng: number; address: string; accuracy?: number }> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        resolve({ lat: 0, lng: 0, address: 'Location unavailable' });
        return;
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            address: `${position.coords.latitude.toFixed(4)}, ${position.coords.longitude.toFixed(4)}`,
            accuracy: position.coords.accuracy
          });
        },
        () => {
          resolve({ lat: 0, lng: 0, address: 'Location unavailable' });
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  }

  /**
   * Speak text using TTS
   */
  private async speak(text: string): Promise<void> {
    try {
      textToSpeech.speak(text);
    } catch (error) {
      console.error('TTS error:', error);
    }
  }

  /**
   * Format time for speech
   */
  private formatTime(isoString: string): string {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }

  /**
   * Format relative time for speech
   */
  private formatRelativeTime(isoString: string): string {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 5) return 'just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays === 1) return 'yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  }

  /**
   * Format well status for speech
   */
  private formatWellStatus(status: string): string {
    const statusMap: Record<string, string> = {
      active: 'active and producing',
      inactive: 'inactive',
      shut_in: 'shut in',
      maintenance: 'undergoing maintenance',
      drilling: 'being drilled',
      completed: 'completed',
      plugged: 'plugged',
      down: 'down'
    };
    return statusMap[status?.toLowerCase()] || status || 'status unknown';
  }

  /**
   * Format well issue for speech
   */
  private formatWellIssue(well: any): string {
    if (well.status === 'down') return 'The well is currently down.';
    if (well.status === 'maintenance') return 'The well is under maintenance.';
    if (well.hasAlert) return 'There is an active alert on this well.';
    if (!well.lastCheck) return 'This well has never been checked.';
    return 'This well is overdue for a check.';
  }

  /**
   * Create error result
   */
  private error(action: string, message: string): VoiceCommandResult {
    const response = `Sorry, I couldn't complete that. ${message}`;
    this.speak(response);
    
    return {
      success: false,
      action,
      message: response,
      speakResponse: true,
      toastType: 'error'
    };
  }
}

// Export singleton instance
export const voiceCommandExecutor = new VoiceCommandExecutorService();

// Export class for testing
export { VoiceCommandExecutorService };
